import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/Shared";
import { toast } from "@/components/ui/sonner";
import { formatDateTime } from "@/lib/format";
import { CheckSquare, CalendarDays, Check, Circle, Link2, GripVertical, Plus, ChevronLeft, ChevronRight } from "lucide-react";

export const TYPE = { task: { Icon: CheckSquare, color: "#2563EB" }, event: { Icon: CalendarDays, color: "#7C3AED" } };
const initials = (n) => (n || "").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
const dayKey = (iso) => { const d = new Date(iso); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
export const todayKey = () => dayKey(new Date().toISOString());

// Overdue · Today · This week · Later · Completed
export function bucketOf(a) {
  if (a.completed) return "Completed";
  const k = dayKey(a.due_at), t = todayKey();
  if (k < t) return "Overdue";
  if (k === t) return "Today";
  const end = new Date(); end.setDate(end.getDate() + (7 - end.getDay()));
  return k <= dayKey(end.toISOString()) ? "This week" : "Later";
}
export const BUCKETS = [{ id: "Overdue", color: "#F83A84" }, { id: "Today", color: "#D97706" }, { id: "This week", color: "#2563EB" }, { id: "Later", color: "#6B7189" }, { id: "Completed", color: "#0B7C5A" }];

export async function toggleTask(a, reload, askComplete) {
  if (!a.completed && askComplete) return askComplete(a);
  try { await api.patch(`/activities/${a.id}`, { completed: !a.completed }); reload(); } catch (e) { toast.error(apiError(e)); }
}

export async function rescheduleTask(a, dayKeyStr, reload) {
  const old = new Date(a.due_at);
  const [y, m, d] = dayKeyStr.split("-").map(Number);
  const next = new Date(y, m - 1, d, old.getHours(), old.getMinutes());
  try { await api.patch(`/activities/${a.id}`, { due_at: next.toISOString() }); toast.success(`Moved to ${next.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}`); reload(); }
  catch (e) { toast.error(apiError(e)); }
}

export function TaskRow({ a, reload, compact = false, askComplete, draggable = false, onDragStart, onDragEnd }) {
  const navigate = useNavigate();
  const T = TYPE[a.type] || TYPE.task;
  return (
    <div draggable={draggable} onDragStart={(e) => { e.dataTransfer.setData("text/plain", a.id); onDragStart?.(a); }} onDragEnd={onDragEnd}
      className={`flex items-start gap-3 ${compact ? "py-2" : "px-3 py-3"} ${a.completed ? "opacity-60" : ""} ${draggable ? "cursor-grab active:cursor-grabbing" : ""}`} data-testid={`task-row-${a.id}`}>
      <button onClick={() => toggleTask(a, reload, askComplete)} className="mt-0.5 w-8 h-8 -ml-1 flex items-center justify-center shrink-0" data-testid={`task-toggle-${a.id}`} title={a.completed ? "Reopen" : "Mark done"}>
        {a.completed ? <Check className="w-5 h-5 text-[#0B7C5A]" /> : <Circle className="w-5 h-5 text-[#C7CBDA]" />}
      </button>
      <div className="min-w-0 flex-1">
        <button onClick={() => navigate(`/tasks/${a.id}`)} className={`text-left text-sm font-medium text-[#101D51] flex items-center gap-1.5 hover:underline max-w-full ${a.completed ? "line-through" : ""}`} data-testid={`task-open-${a.id}`}><T.Icon className="w-3.5 h-3.5 shrink-0" style={{ color: T.color }} /><span className="truncate">{a.subject}</span></button>
        <div className="text-[11px] text-[#6B7189] flex flex-wrap items-center gap-x-2 mt-0.5">
          <span className={a.overdue ? "text-[#F83A84] font-medium" : ""}>{a.overdue ? "Overdue · " : ""}{formatDateTime(a.due_at)}</span>
          {a.record_label && <button onClick={() => navigate(a.record_path)} className="flex items-center gap-1 text-[#1D4ED8] hover:underline min-h-[24px]" data-testid={`task-link-${a.id}`}><Link2 className="w-3 h-3" />{a.record_label}</button>}
        </div>
      </div>
      <span title={a.assigned_name || "Unassigned"} className={`w-7 h-7 rounded-full text-[10px] font-bold flex items-center justify-center shrink-0 ${a.assigned_name ? "bg-[#101D51] text-white" : "border border-dashed border-[#C7CBDA] text-[#C7CBDA]"}`} data-testid={`task-owner-${a.id}`}>{a.assigned_name ? initials(a.assigned_name) : "?"}</span>
    </div>);
}

// Kanban grouped by status bucket or by owner. Drag to Completed/open lane toggles done; drag between owner lanes reassigns.
export function TaskKanban({ items, users, groupBy, reload, askComplete }) {
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);
  const lanes = groupBy === "owner"
    ? [...users.map((u) => ({ id: u.id, label: u.name, color: "#101D51" })), { id: "unassigned", label: "Unassigned", color: "#6B7189" }]
    : BUCKETS.map((b) => ({ id: b.id, label: b.id, color: b.color }));
  const laneOf = (a) => (groupBy === "owner" ? a.assigned_to || "unassigned" : bucketOf(a));
  const drop = async (lane) => {
    setOver(null);
    if (!drag || laneOf(drag) === lane) return setDrag(null);
    try {
      if (groupBy === "owner") await api.patch(`/activities/${drag.id}`, { assigned_to: lane === "unassigned" ? null : lane });
      else if (lane === "Completed") { const t = drag; setDrag(null); return askComplete(t); }
      else if (drag.completed) await api.patch(`/activities/${drag.id}`, { completed: false });
      else { toast.error("Change the due date to move between date lanes"); setDrag(null); return; }
      reload();
    } catch (e) { toast.error(apiError(e)); }
    setDrag(null);
  };
  return (
    <div className="flex gap-3 overflow-x-auto pb-4 snap-x snap-mandatory sm:snap-proximity no-scrollbar sm:[scrollbar-width:auto]" data-testid="task-kanban">
      {lanes.map((l) => {
        const rows = items.filter((a) => laneOf(a) === l.id);
        const slug = l.id.replace(/\s+/g, "-").toLowerCase();
        return (
          <div key={l.id} className="w-[84vw] sm:w-72 shrink-0 snap-center sm:snap-start" data-testid={`task-col-${slug}`}
            onDragOver={(e) => { e.preventDefault(); setOver(l.id); }} onDragLeave={() => setOver(null)} onDrop={(e) => { e.preventDefault(); drop(l.id); }}>
            <div className="flex items-center justify-between px-1 mb-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-[#101D51] flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: l.color }} />{l.label}<span className="ml-1 bg-[#E6E8F0] text-[#6B7189] rounded-full px-1.5 text-[10px]">{rows.length}</span></div>
            </div>
            <div className={`space-y-2 min-h-[80px] rounded-xl p-2 transition-colors ${over === l.id && drag ? "bg-[#0B7C5A]/10 ring-2 ring-[#0B7C5A]/40" : "bg-[#F5F6FA]"}`} data-testid={`task-drop-${slug}`}>
              {rows.map((a) => (
                <Card key={a.id} draggable onDragStart={(e) => { e.dataTransfer.setData("text/plain", a.id); setDrag(a); }} onDragEnd={() => { setDrag(null); setOver(null); }}
                  className={`cursor-grab active:cursor-grabbing ${drag?.id === a.id ? "opacity-40" : ""}`} data-testid={`task-card-${a.id}`}>
                  <div className="flex items-center gap-1 px-2 pt-1 text-[#C7CBDA]"><GripVertical className="w-3 h-3" /></div>
                  <TaskRow a={a} reload={reload} askComplete={askComplete} />
                </Card>))}
              {rows.length === 0 && <div className="text-xs text-[#6B7189] text-center py-6">{drag ? "Drop here" : "—"}</div>}
            </div>
          </div>);
      })}
    </div>);
}

// Week-strip calendar (Zoho-style): month title + Today, 7 day pills with dots, arrows/swipe move a week, day sections below.
// Desktop: drag a task row onto a day pill to reschedule it.
export function TaskCalendar({ items, day, setDay, reload, askComplete, onNew }) {
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);
  const touch = useRef(null);
  const sel = new Date(day + "T00:00:00");
  const start = new Date(sel); start.setDate(sel.getDate() - sel.getDay());
  const days = Array.from({ length: 7 }, (_, i) => { const x = new Date(start); x.setDate(start.getDate() + i); return x; });
  const shift = (n) => { const x = new Date(sel); x.setDate(sel.getDate() + n); setDay(dayKey(x.toISOString())); };
  const byDay = {};
  items.forEach((a) => (byDay[dayKey(a.due_at)] = [...(byDay[dayKey(a.due_at)] || []), a]));
  const todays = byDay[day] || [];
  const t = todayKey();
  const dropOn = (k) => { if (drag && dayKey(drag.due_at) !== k) rescheduleTask(drag, k, reload); setDrag(null); setOver(null); };
  const Section = ({ title, type, rows }) => (
    <Card className="overflow-hidden" data-testid={`cal-section-${type}`}>
      <div className="px-4 py-2.5 flex items-center justify-between border-b border-[#F0F1F5]"><span className="font-semibold text-[#101D51]">{title}</span>
        <button onClick={() => onNew(type, day)} className="w-9 h-9 rounded-full hover:bg-[#F5F6FA] flex items-center justify-center text-[#2563EB]" data-testid={`cal-add-${type}`}><Plus className="w-5 h-5" /></button></div>
      <div className="divide-y divide-[#F0F1F5] px-3">
        {rows.length === 0 && <div className="text-sm text-[#6B7189] text-center py-5">No {title.toLowerCase()} for this day <button onClick={() => onNew(type, day)} className="text-[#2563EB] font-medium ml-1" data-testid={`cal-empty-add-${type}`}>+ {title.slice(0, -1)}</button></div>}
        {rows.map((a) => <TaskRow key={a.id} a={a} reload={reload} askComplete={askComplete} compact draggable onDragStart={setDrag} onDragEnd={() => { setDrag(null); setOver(null); }} />)}
      </div>
    </Card>);
  return (
    <div className="space-y-3" data-testid="task-calendar">
      <Card className="p-3 sm:p-4 select-none" onTouchStart={(e) => (touch.current = e.touches[0].clientX)}
        onTouchEnd={(e) => { if (touch.current == null) return; const dx = e.changedTouches[0].clientX - touch.current; touch.current = null; if (Math.abs(dx) > 50) shift(dx < 0 ? 7 : -7); }}>
        <div className="flex items-center justify-between mb-3">
          <div className="text-lg font-bold text-[#101D51]" data-testid="cal-month-title">{sel.toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</div>
          <div className="flex items-center gap-1">
            <button onClick={() => shift(-7)} className="w-9 h-9 rounded-lg hover:bg-[#F5F6FA] flex items-center justify-center" data-testid="cal-prev-week"><ChevronLeft className="w-5 h-5" /></button>
            <button onClick={() => setDay(t)} className="h-9 px-3 rounded-lg text-sm font-semibold text-[#2563EB] hover:bg-[#F5F6FA]" data-testid="cal-today">Today</button>
            <button onClick={() => shift(7)} className="w-9 h-9 rounded-lg hover:bg-[#F5F6FA] flex items-center justify-center" data-testid="cal-next-week"><ChevronRight className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1" data-testid="cal-week-strip">
          {days.map((x) => {
            const k = dayKey(x.toISOString()), n = (byDay[k] || []).filter((a) => !a.completed).length, active = k === day;
            return (
              <button key={k} onClick={() => setDay(k)} data-testid={`cal-day-${k}`}
                onDragOver={(e) => { e.preventDefault(); setOver(k); }} onDragLeave={() => setOver(null)} onDrop={(e) => { e.preventDefault(); dropOn(k); }}
                className={`flex flex-col items-center py-2 rounded-xl min-h-[64px] transition-colors ${active ? "bg-[#2563EB] text-white" : k === t ? "ring-1 ring-[#101D51]/40 text-[#101D51]" : "text-[#101D51] hover:bg-[#F5F6FA]"} ${over === k && drag ? "ring-2 ring-[#0B7C5A] bg-[#0B7C5A]/10" : ""}`}>
                <span className={`text-[10px] uppercase ${active ? "text-white/80" : "text-[#6B7189]"}`}>{x.toLocaleDateString("en-IN", { weekday: "short" })}</span>
                <span className="text-lg font-semibold leading-tight">{x.getDate()}</span>
                <span className={`w-1.5 h-1.5 rounded-full mt-0.5 ${n ? (active ? "bg-white" : "bg-[#2563EB]") : "bg-transparent"}`} />
              </button>);
          })}
        </div>
        {drag && <div className="text-[11px] text-[#0B7C5A] text-center mt-2" data-testid="cal-drag-hint">Drop on a day to reschedule "{drag.subject}"</div>}
      </Card>
      <Section title="Events" type="event" rows={todays.filter((a) => a.type === "event")} />
      <Section title="Tasks" type="task" rows={todays.filter((a) => a.type !== "event")} />
    </div>);
}
